import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';
import { Shield, Plus, Edit2, Trash2, Save, Users, Lock, Loader2, CheckCircle2, Eye, Menu } from 'lucide-react';
import { toast } from 'sonner';
import { useTenant } from '@/contexts/TenantContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const api = axios.create({ baseURL: API_URL });
api.interceptors.request.use(c => { const t = localStorage.getItem('token'); if (t) c.headers.Authorization = `Bearer ${t}`; return c; });

const RBACSettings = () => {
  const { currentTenant, isSuperAdmin, isTenantAdmin, userRole } = useTenant();
  const [loading, setLoading] = useState(true);
  const [systemRoles, setSystemRoles] = useState([]);
  const [customRoles, setCustomRoles] = useState([]);
  const [permCategories, setPermCategories] = useState({});
  const [availableMenus, setAvailableMenus] = useState([]);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  const [newRole, setNewRole] = useState({ name: '', description: '', permissions: [], visible_menus: [] });
  const [saving, setSaving] = useState(false);

  const canManageRoles = isSuperAdmin || userRole === 'admin' || userRole === 'owner' || isTenantAdmin?.();
  const allMenuIds = availableMenus.map(m => m.menu_id);

  useEffect(() => { if (currentTenant?.id) loadRoles(); }, [currentTenant?.id]);

  const loadRoles = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/api/users/roles', { params: { tenant_id: currentTenant?.id } });
      setSystemRoles(data.system_roles || []);
      setCustomRoles(data.custom_roles || []);
      setPermCategories(data.permission_categories || {});
      setAvailableMenus(data.available_menus || []);
    } catch {
      toast.error('Failed to load roles');
    } finally {
      setLoading(false);
    }
  };

  const handleEditRole = (role) => {
    setEditingRole({ ...role, permissions: [...(role.permissions || [])], visible_menus: [...(role.visible_menus || allMenuIds)] });
    setShowEditDialog(true);
  };

  const togglePerm = (permKey, setter) => {
    setter(prev => ({
      ...prev,
      permissions: prev.permissions.includes(permKey)
        ? prev.permissions.filter(p => p !== permKey)
        : [...prev.permissions, permKey]
    }));
  };

  const toggleMenu = (menuId, setter) => {
    setter(prev => {
      const has = prev.visible_menus.includes(menuId);
      return { ...prev, visible_menus: has ? prev.visible_menus.filter(m => m !== menuId) : [...prev.visible_menus, menuId] };
    });
  };

  const handleSaveRole = async () => {
    setSaving(true);
    try {
      await api.put(`/api/users/roles/${editingRole.id}`, {
        tenant_id: currentTenant?.id,
        permissions: editingRole.permissions,
        visible_menus: editingRole.visible_menus,
        description: editingRole.description,
        name: editingRole.name,
      });
      toast.success('Role updated');
      setShowEditDialog(false);
      loadRoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update role');
    } finally {
      setSaving(false);
    }
  };

  const handleCreateRole = async () => {
    if (!newRole.name.trim()) { toast.error('Role name is required'); return; }
    setSaving(true);
    try {
      await api.post('/api/users/roles', {
        tenant_id: currentTenant?.id,
        name: newRole.name,
        description: newRole.description,
        permissions: newRole.permissions,
        visible_menus: newRole.visible_menus.length > 0 ? newRole.visible_menus : allMenuIds,
      });
      toast.success(`Role "${newRole.name}" created`);
      setShowCreateDialog(false);
      setNewRole({ name: '', description: '', permissions: [], visible_menus: [] });
      loadRoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create role');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRole = async (roleId) => {
    try {
      await api.delete(`/api/users/roles/${roleId}`, { params: { tenant_id: currentTenant?.id } });
      toast.success('Role deleted');
      loadRoles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete role');
    }
  };

  const PermissionGrid = ({ perms, onToggle, isSystem }) => (
    <div className="space-y-5">
      {Object.entries(permCategories).map(([cat, catPerms]) => (
        <div key={cat}>
          <h4 className="font-medium text-sm text-muted-foreground mb-2">{cat}</h4>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            {catPerms.map(p => (
              <div key={p.key} className="flex items-center space-x-2">
                <Checkbox
                  id={`perm-${p.key}`}
                  checked={perms.includes(p.key)}
                  onCheckedChange={() => onToggle(p.key)}
                  disabled={isSystem}
                  data-testid={`perm-${p.key}`}
                />
                <label htmlFor={`perm-${p.key}`} className="text-sm cursor-pointer">{p.label}</label>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );

  const MenuVisibilityGrid = ({ visibleMenus, onToggle, isSystem }) => (
    <div className="space-y-2">
      {availableMenus.map(menu => {
        const isVisible = visibleMenus.includes(menu.menu_id);
        return (
          <div key={menu.menu_id} className="border rounded-lg overflow-hidden">
            <div className="flex items-center justify-between p-3 bg-gray-50">
              <span className={`font-medium text-sm ${!isVisible ? 'text-gray-400' : ''}`}>{menu.label}</span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-gray-500">{isVisible ? 'Visible' : 'Hidden'}</span>
                <Switch checked={isVisible} onCheckedChange={() => onToggle(menu.menu_id)} disabled={isSystem} data-testid={`menu-toggle-${menu.menu_id}`} />
              </div>
            </div>
            {menu.submenus?.length > 0 && isVisible && (
              <div className="p-3 space-y-1.5 border-t bg-white">
                {menu.submenus.map(sub => (
                  <div key={sub.menu_id} className="flex items-center justify-between pl-4 text-sm text-muted-foreground">
                    <span>{sub.label}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  // Role edit/create dialog content with tabs
  const RoleDialogContent = ({ role, setter, isSystem, isCreate }) => (
    <Tabs defaultValue="permissions" className="mt-2">
      <TabsList className="grid grid-cols-2 w-full max-w-[320px]">
        <TabsTrigger value="permissions" className="flex items-center gap-1">
          <Shield className="w-3 h-3" /> Permissions
        </TabsTrigger>
        <TabsTrigger value="menus" className="flex items-center gap-1" data-testid="menu-visibility-tab">
          <Eye className="w-3 h-3" /> Menu Visibility
        </TabsTrigger>
      </TabsList>
      <TabsContent value="permissions" className="mt-4">
        <PermissionGrid
          perms={role.permissions}
          onToggle={(k) => togglePerm(k, setter)}
          isSystem={isSystem}
        />
      </TabsContent>
      <TabsContent value="menus" className="mt-4">
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 text-sm text-amber-800">
          Toggle which menus are visible in the sidebar for users with this role.
        </div>
        <MenuVisibilityGrid
          visibleMenus={role.visible_menus || []}
          onToggle={(id) => toggleMenu(id, setter)}
          isSystem={isSystem}
        />
      </TabsContent>
    </Tabs>
  );

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;
  }

  const allRoles = [...systemRoles, ...customRoles];

  return (
    <div className="space-y-6" data-testid="rbac-settings">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Shield className="w-5 h-5 text-indigo-600" />
            Roles & Permissions (RBAC)
          </h2>
          <p className="text-sm text-muted-foreground mt-1">
            Configure access levels, permissions, and menu visibility for user roles
          </p>
        </div>
        {canManageRoles && (
          <Button onClick={() => { setNewRole({ name: '', description: '', permissions: [], visible_menus: [...allMenuIds] }); setShowCreateDialog(true); }} data-testid="create-role-btn">
            <Plus className="w-4 h-4 mr-2" /> Create Role
          </Button>
        )}
      </div>

      {/* Roles Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {allRoles.map(role => (
          <Card key={role.id} className="relative" data-testid={`role-card-${role.id}`}>
            {role.is_system && (
              <Badge variant="secondary" className="absolute top-3 right-3 text-xs">System</Badge>
            )}
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2">
                <Lock className="w-4 h-4" />
                {role.name}
              </CardTitle>
              <CardDescription className="text-xs">{role.description}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground flex items-center gap-1"><Users className="w-3.5 h-3.5" /> Users</span>
                  <Badge variant="outline">{role.user_count || 0}</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> Permissions</span>
                  <Badge variant="outline">{(role.permissions || []).length}</Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground flex items-center gap-1"><Menu className="w-3.5 h-3.5" /> Menus</span>
                  <Badge variant="outline">{(role.visible_menus || []).length} / {allMenuIds.length}</Badge>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="flex-1" onClick={() => handleEditRole(role)} data-testid={`edit-role-${role.id}`}>
                    <Edit2 className="w-3 h-3 mr-1" /> {role.is_system ? 'View' : 'Edit'}
                  </Button>
                  {!role.is_system && canManageRoles && (
                    <Button variant="outline" size="sm" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteRole(role.id)} data-testid={`delete-role-${role.id}`}>
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Edit Role Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingRole?.is_system ? 'View' : 'Edit'} Role: {editingRole?.name}</DialogTitle>
            <DialogDescription>
              {editingRole?.is_system ? 'System roles — permissions and menus shown for reference.' : 'Update permissions and menu visibility.'}
            </DialogDescription>
          </DialogHeader>
          {editingRole && (
            <RoleDialogContent role={editingRole} setter={setEditingRole} isSystem={editingRole.is_system} />
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
            {!editingRole?.is_system && (
              <Button onClick={handleSaveRole} disabled={saving} data-testid="save-role-btn">
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save Changes
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Role Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Custom Role</DialogTitle>
            <DialogDescription>Define permissions and menu visibility for a new role.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Role Name</Label>
                <Input value={newRole.name} onChange={e => setNewRole(prev => ({ ...prev, name: e.target.value }))} placeholder="e.g. Security Analyst" data-testid="new-role-name-input" />
              </div>
              <div>
                <Label>Description</Label>
                <Input value={newRole.description} onChange={e => setNewRole(prev => ({ ...prev, description: e.target.value }))} placeholder="Brief description" data-testid="new-role-description-input" />
              </div>
            </div>
            <RoleDialogContent role={newRole} setter={setNewRole} isSystem={false} isCreate />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
            <Button onClick={handleCreateRole} disabled={saving || !newRole.name.trim()} data-testid="submit-create-role-btn">
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              Create Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RBACSettings;
